// src/utils/tabGuard.ts

/**
 * TabGuard v2
 *
 * Garante "uma aba por navegador" usando localStorage + heartbeat.
 *
 * - Chave em localStorage: "eva_active_tab_v1"
 * - Estrutura: { id: string, lastSeen: number }
 * - Cada aba tem um id próprio.
 * - A aba "dona" atual fica atualizando lastSeen a cada HEARTBEAT_MS.
 * - Se outra aba abrir e encontrar um dono recente (não expirado), é BLOQUEADA.
 * - Se o dono sumir (fechou/crashou e TTL estourou), a próxima aba que abrir assume.
 *
 * Tem TTL justamente pra não ficar travado se o browser matar a aba sem disparar unload.
 */

const ACTIVE_KEY = "eva_active_tab_v1";

// com 5s de heartbeat, 30s de TTL dá margem pra aba em background ser “throttled”
const HEARTBEAT_MS = 5000;
const STALE_MS = 30000;

type ActiveRecord = {
  id: string;
  lastSeen: number;
};

function now() {
  return Date.now();
}

function readActive(): ActiveRecord | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveRecord;
    if (
      !parsed ||
      typeof parsed.id !== "string" ||
      typeof parsed.lastSeen !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeActive(rec: ActiveRecord) {
  try {
    localStorage.setItem(ACTIVE_KEY, JSON.stringify(rec));
  } catch {
    // ignore
  }
}

function clearActiveIfOwner(id: string) {
  try {
    const current = readActive();
    if (current && current.id === id) {
      localStorage.removeItem(ACTIVE_KEY);
    }
  } catch {
    // ignore
  }
}

export class TabGuard {
  private id: string;
  private resolved = false;
  private allowed = false;
  private started = false;
  private heartbeatTimer: number | null = null;

  constructor() {
    // id fixo por instância de TabGuard (por aba)
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      this.id = crypto.randomUUID();
    } else {
      this.id = `tab-${now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  }

  /**
   * Inicializa o guard.
   * - callback(true): esta aba é a DONA e pode rodar o app.
   * - callback(false): já existe aba ativa → mostrar tela de "Aba já ativa".
   */
  init(callback: (allowed: boolean) => void) {
    // proteção contra StrictMode (effect rodando 2x)
    if (this.started) {
      callback(this.allowed);
      return;
    }
    this.started = true;

    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      // ambiente bizarro → não bloqueia nada
      this.allowed = true;
      this.resolved = true;
      callback(true);
      return;
    }

    try {
      const current = readActive();
      const t = now();

      if (!current) {
        // ninguém dono → esta aba assume
        this.becomeOwner();
        callback(true);
        return;
      }

      const isStale = t - current.lastSeen > STALE_MS;

      if (isStale) {
        // dono antigo expirou → esta aba assume
        this.becomeOwner();
        callback(true);
        return;
      }

      if (current.id === this.id) {
        // já sou o dono (ex.: reload)
        this.becomeOwner();
        callback(true);
        return;
      }

      // Existe outro dono recente com id diferente → esta aba é BLOQUEADA
      this.allowed = false;
      this.resolved = true;
      callback(false);
    } catch {
      // se der algum erro inesperado, não bloqueia
      this.allowed = true;
      this.resolved = true;
      callback(true);
    }
  }

  private becomeOwner() {
    this.allowed = true;
    this.resolved = true;

    // grava como dono
    writeActive({ id: this.id, lastSeen: now() });

    // heartbeat pra manter a posse enquanto a aba está viva
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
    }
    this.heartbeatTimer = window.setInterval(() => {
      // só atualiza se ainda formos o dono
      const current = readActive();
      if (!current || current.id === this.id) {
        writeActive({ id: this.id, lastSeen: now() });
      }
    }, HEARTBEAT_MS) as unknown as number;

    // ao fechar, se ainda formos o dono, limpamos a chave
    const unloadHandler = () => {
      clearActiveIfOwner(this.id);
    };

    window.addEventListener("beforeunload", unloadHandler);
    window.addEventListener("unload", unloadHandler);

    // guardamos pra remover depois no release
    (this as any)._unloadHandler = unloadHandler;
  }

  /**
   * Libera recursos (limpa timers e listeners).
   * Não precisa manter nada se a aba for desmontada.
   */
  release() {
    this.resolved = true;

    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    const unloadHandler = (this as any)._unloadHandler as
      | (() => void)
      | undefined;

    if (unloadHandler) {
      window.removeEventListener("beforeunload", unloadHandler);
      window.removeEventListener("unload", unloadHandler);
    }
  }
}
